// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * A sample portfolio of six completed projects.
 *
 * Portfolio Insights is the one thing in OpenEstimate a competitor cannot buy
 * from a data vendor: it is built from the firm's own job history. That is
 * also its weakness — it says nothing until the fifth or sixth project, which
 * is exactly the commitment a new estimator has not made yet. They open it,
 * see "No projects to roll up yet", and never learn what it was for.
 *
 * This is the answer: a believable portfolio an estimator can look at on day
 * one and recognise as their own working life. It is labelled as sample data
 * wherever it appears, and it never touches the database.
 *
 * Everything comes from a seeded PRNG, so the numbers are identical on every
 * render, in every browser, and in the tests. Demo data that shifted under you
 * would be worse than none.
 */

import { ALL_DIVISIONS } from "../projectData";
import { computePortfolioFinancials } from "./portfolioFinancials";
import type { BuyoutItem } from "../types";
import type { Bid, BidPartner, BidScope } from "../projectData";

export interface DemoProject {
  id: string;
  name: string;
  dateISO: string;
  divisions: any[];
  itemState: Record<string, any>;
  overhead: number;
  profit: number;
  contingency: number;
  buildingArea: number;
  bids: Bid[];
  bidPartners: BidPartner[];
  bidScopes: BidScope[];
  buyoutItems: BuyoutItem[];
}

/** mulberry32 — small, fast, stable across engines. */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Trade partners ──────────────────────────────────────────────────────────
// Deliberately recurring across projects. A partner who appears once has no
// win rate worth reading; the scorecard only gets interesting when the same
// names come back job after job, which is what actually happens at a firm.

// Every trade carries at least two names. A trade with one bidder wins every
// package it is offered, and a scorecard full of 100% win rates tells an
// estimator nothing — it just means nobody else was asked.

const PARTNERS: { id: string; name: string; trade: string; divisionId: string; bias: number }[] = [
  { id: "dp_con",  name: "Keystone Concrete LLC",         trade: "Concrete",        divisionId: "div3", bias: 0.018 },
  { id: "dp_con2", name: "Delaware Valley Concrete",      trade: "Concrete",        divisionId: "div3", bias: -0.01 },
  { id: "dp_con3", name: "Pennypack Foundations",         trade: "Concrete",        divisionId: "div3", bias: 0.03 },
  { id: "dp_mas",  name: "Chestnut Hill Masonry",         trade: "Masonry",         divisionId: "div4", bias: -0.006 },
  { id: "dp_mas2", name: "Fairmount Stone & Brick",       trade: "Masonry",         divisionId: "div4", bias: 0.016 },
  { id: "dp_stl",  name: "Lancaster Steel Fabricators",   trade: "Structural Steel", divisionId: "div5", bias: 0.012 },
  { id: "dp_stl2", name: "Delco Iron Works",              trade: "Structural Steel", divisionId: "div5", bias: -0.009 },
  { id: "dp_frm",  name: "Bryn Mawr Framing Co.",         trade: "Rough Carpentry", divisionId: "div6", bias: -0.012 },
  { id: "dp_frm2", name: "Conshohocken Carpentry",        trade: "Rough Carpentry", divisionId: "div6", bias: 0.022 },
  { id: "dp_frm3", name: "Valley Forge Timberworks",      trade: "Rough Carpentry", divisionId: "div6", bias: 0.004 },
  { id: "dp_roo",  name: "Ardmore Roofing & Sheet Metal", trade: "Roofing",         divisionId: "div7", bias: 0.01 },
  { id: "dp_roo2", name: "Manayunk Roofing Co.",          trade: "Roofing",         divisionId: "div7", bias: -0.012 },
  { id: "dp_win",  name: "Main Line Window & Door",       trade: "Openings",        divisionId: "div8", bias: -0.008 },
  { id: "dp_win2", name: "Paoli Glass & Glazing",         trade: "Openings",        divisionId: "div8", bias: 0.018 },
  { id: "dp_drw",  name: "Haverford Drywall & Trim",      trade: "Finishes",        divisionId: "div9", bias: 0.014 },
  { id: "dp_drw2", name: "Wayne Interior Finishes",       trade: "Finishes",        divisionId: "div9", bias: -0.006 },
  { id: "dp_plm",  name: "Schuylkill Plumbing",           trade: "Plumbing",        divisionId: "div22", bias: -0.016 },
  { id: "dp_plm2", name: "Bala Cynwyd Plumbing & Heating", trade: "Plumbing",       divisionId: "div22", bias: 0.009 },
  { id: "dp_hvc",  name: "Narberth Mechanical",           trade: "HVAC",            divisionId: "div23", bias: 0.007 },
  { id: "dp_hvc2", name: "Gladwyne Climate Control",      trade: "HVAC",            divisionId: "div23", bias: -0.012 },
  { id: "dp_ele",  name: "Wissahickon Electric",          trade: "Electrical",      divisionId: "div26", bias: -0.014 },
  { id: "dp_ele2", name: "Bridgeport Electrical Services", trade: "Electrical",     divisionId: "div26", bias: 0.011 },
  { id: "dp_sit",  name: "Radnor Site Works",             trade: "Site / Grading",  divisionId: "div32", bias: 0.013 },
  { id: "dp_sit2", name: "Montgomery Excavating",         trade: "Site / Grading",  divisionId: "div32", bias: -0.008 },
];

// ── The catalogue every estimate is built from ──────────────────────────────
// Unit costs are the whole point of the Cost Library tab, so they are
// plausible rather than round. qtyPer1k scales with building area (per 1,000
// SF); qtyFixed does not scale.

interface CatalogItem {
  desc: string; unit: string; unitCost: number;
  qtyPer1k?: number; qtyFixed?: number; status?: string;
}
interface CatalogSub { name: string; costCode: string; items: CatalogItem[] }

const CATALOG: Record<string, CatalogSub[]> = {
  div1: [
    { name: "General Conditions", costCode: "01-200", items: [
      { desc: "Project Superintendent", unit: "MO", unitCost: 15800, qtyFixed: 12 },
      { desc: "Project Management", unit: "MO", unitCost: 9400, qtyFixed: 12 },
      { desc: "Temporary Facilities & Utilities", unit: "LS", unitCost: 38500, qtyFixed: 1 },
      { desc: "Builders Risk & General Liability", unit: "LS", unitCost: 41200, qtyFixed: 1 },
    ] },
    { name: "Permits & Fees", costCode: "01-100", items: [
      { desc: "Building Permit", unit: "LS", unitCost: 62000, qtyFixed: 1, status: "Allowance" },
      { desc: "Special Inspections & Testing", unit: "LS", unitCost: 17400, qtyFixed: 1 },
    ] },
  ],
  div2: [
    { name: "Demolition", costCode: "02-050", items: [
      { desc: "Selective Interior Demolition", unit: "SF", unitCost: 6.25, qtyPer1k: 400 },
      { desc: "Hazmat Survey & Abatement", unit: "LS", unitCost: 14800, qtyFixed: 1, status: "Allowance" },
    ] },
  ],
  div3: [
    { name: "Building Concrete", costCode: "03-100", items: [
      { desc: "Spread Footings 4000 PSI", unit: "CY", unitCost: 487, qtyPer1k: 11 },
      { desc: "Foundation Walls, Formed", unit: "SF", unitCost: 32.5, qtyPer1k: 220 },
      { desc: "Slab on Grade, Reinforced", unit: "SF", unitCost: 11.8, qtyPer1k: 380 },
      { desc: "Reinforcing Steel, Placed", unit: "TON", unitCost: 3150, qtyPer1k: 1.4 },
    ] },
    { name: "Site Concrete", costCode: "03-200", items: [
      { desc: "Exterior Slabs & Walks", unit: "SF", unitCost: 16.4, qtyPer1k: 95 },
    ] },
  ],
  div4: [
    { name: "Unit Masonry", costCode: "04-200", items: [
      { desc: "Stone Veneer, Full Bed", unit: "SF", unitCost: 68, qtyPer1k: 130 },
      { desc: "Masonry Chimney", unit: "LS", unitCost: 34500, qtyFixed: 1 },
    ] },
  ],
  div5: [
    { name: "Structural Steel", costCode: "05-100", items: [
      { desc: "Structural Steel, Fabricated & Erected", unit: "TON", unitCost: 6200, qtyPer1k: 0.9 },
      { desc: "Steel Moment Frames", unit: "EA", unitCost: 14200, qtyPer1k: 0.5 },
    ] },
  ],
  div6: [
    { name: "Rough Carpentry", costCode: "06-100", items: [
      { desc: "Floor Framing, Engineered Joist", unit: "SF", unitCost: 14.2, qtyPer1k: 640 },
      { desc: "Wall Framing, Exterior", unit: "SF", unitCost: 12.8, qtyPer1k: 520 },
      { desc: "Roof Framing, Cut & Stacked", unit: "SF", unitCost: 18.6, qtyPer1k: 410 },
      { desc: "Exterior Sheathing & WRB", unit: "SF", unitCost: 7.4, qtyPer1k: 520 },
    ] },
    { name: "Finish Carpentry", costCode: "06-200", items: [
      { desc: "Interior Trim & Millwork", unit: "SF", unitCost: 22.5, qtyPer1k: 300 },
      { desc: "Custom Cabinetry Allowance", unit: "LS", unitCost: 118000, qtyFixed: 1, status: "Allowance" },
    ] },
  ],
  div7: [
    { name: "Roofing", costCode: "07-300", items: [
      { desc: "Standing Seam Metal Roof", unit: "SQ", unitCost: 1850, qtyPer1k: 3.2 },
      { desc: "Membrane Roofing, Low Slope", unit: "SQ", unitCost: 1240, qtyPer1k: 0.9 },
    ] },
    { name: "Insulation & Air Barrier", costCode: "07-200", items: [
      { desc: "Closed Cell Spray Foam", unit: "SF", unitCost: 4.85, qtyPer1k: 680 },
      { desc: "Fluid Applied Air Barrier", unit: "SF", unitCost: 3.6, qtyPer1k: 520 },
    ] },
  ],
  div8: [
    { name: "Windows & Doors", costCode: "08-500", items: [
      { desc: "Aluminum Clad Wood Windows", unit: "EA", unitCost: 3850, qtyPer1k: 5.5 },
      { desc: "Exterior Door Assemblies", unit: "EA", unitCost: 6400, qtyPer1k: 0.8 },
      { desc: "Interior Doors, Solid Core", unit: "EA", unitCost: 1180, qtyPer1k: 3.2 },
    ] },
  ],
  div9: [
    { name: "Drywall & Plaster", costCode: "09-250", items: [
      { desc: "Gypsum Board, Hung & Finished", unit: "SF", unitCost: 4.95, qtyPer1k: 2400 },
      { desc: "Level 5 Finish, Feature Areas", unit: "SF", unitCost: 7.2, qtyPer1k: 280 },
    ] },
    { name: "Flooring", costCode: "09-600", items: [
      { desc: "White Oak Flooring, Site Finished", unit: "SF", unitCost: 26.5, qtyPer1k: 620 },
      { desc: "Tile, Stone & Setting", unit: "SF", unitCost: 44, qtyPer1k: 180 },
    ] },
    { name: "Painting", costCode: "09-900", items: [
      { desc: "Interior Paint, Walls & Trim", unit: "SF", unitCost: 3.85, qtyPer1k: 2400 },
    ] },
  ],
  div22: [
    { name: "Plumbing", costCode: "22-100", items: [
      { desc: "Plumbing Rough-In", unit: "SF", unitCost: 13.4, qtyPer1k: 1000 },
      { desc: "Fixtures & Trim Allowance", unit: "LS", unitCost: 86000, qtyFixed: 1, status: "Allowance" },
    ] },
  ],
  div23: [
    { name: "HVAC", costCode: "23-100", items: [
      { desc: "Ducted Forced Air Systems", unit: "SF", unitCost: 19.8, qtyPer1k: 1000 },
      { desc: "Radiant Floor Heating", unit: "SF", unitCost: 16.2, qtyPer1k: 260 },
      { desc: "Controls & Zoning", unit: "LS", unitCost: 28400, qtyFixed: 1 },
    ] },
  ],
  div26: [
    { name: "Electrical", costCode: "26-100", items: [
      { desc: "Electrical Rough-In & Devices", unit: "SF", unitCost: 17.6, qtyPer1k: 1000 },
      { desc: "Lighting Fixtures Allowance", unit: "LS", unitCost: 74000, qtyFixed: 1, status: "Allowance" },
      { desc: "Generator & Transfer Switch", unit: "LS", unitCost: 41500, qtyFixed: 1 },
    ] },
  ],
  div32: [
    { name: "Site Improvements", costCode: "32-100", items: [
      { desc: "Driveway, Bituminous", unit: "SF", unitCost: 9.4, qtyPer1k: 180 },
      { desc: "Landscape & Planting Allowance", unit: "LS", unitCost: 95000, qtyFixed: 1, status: "Allowance" },
      { desc: "Site Drainage & Stormwater", unit: "LS", unitCost: 52000, qtyFixed: 1 },
    ] },
  ],
};

// ── The six jobs ────────────────────────────────────────────────────────────
// Varied on purpose: size, markup discipline and age all differ, so the
// Portfolio tab shows a spread rather than six versions of one project.

/** Months back from today. Hardcoded dates would quietly rot: a sample
 *  portfolio whose newest job is "20mo ago" reads as abandoned, which is the
 *  opposite of the impression it exists to create. */
function monthsAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - n);
  // Mid-month, so a bid dated up to three weeks earlier stays in a sane place.
  d.setUTCDate(18);
  return d.toISOString().slice(0, 10);
}

interface ProjectSpec {
  id: string; name: string; area: number; dateISO: string;
  overhead: number; profit: number; contingency: number;
  divisionIds: string[];
  /** Multiplies every unit cost — a renovation prices differently from new build. */
  priceFactor: number;
  seed: number;
}

const SPECS: ProjectSpec[] = [
  { id: "demo_1", name: "Mill Creek Residence", area: 11200, dateISO: monthsAgo(29),
    overhead: 10, profit: 8, contingency: 5, priceFactor: 1.0, seed: 101,
    divisionIds: ["div1","div3","div4","div6","div7","div8","div9","div22","div23","div26","div32"] },

  { id: "demo_2", name: "Gulph Mills Carriage House", area: 3400, dateISO: monthsAgo(24),
    overhead: 12, profit: 9, contingency: 6, priceFactor: 1.08, seed: 202,
    divisionIds: ["div1","div3","div6","div7","div8","div9","div22","div23","div26"] },

  { id: "demo_3", name: "Sycamore Lane Renovation", area: 6800, dateISO: monthsAgo(19),
    overhead: 11, profit: 8, contingency: 8, priceFactor: 1.15, seed: 303,
    divisionIds: ["div1","div2","div6","div7","div8","div9","div22","div23","div26"] },

  { id: "demo_4", name: "Waverly Road Estate", area: 18600, dateISO: monthsAgo(13),
    overhead: 9, profit: 10, contingency: 5, priceFactor: 0.96, seed: 404,
    divisionIds: ["div1","div3","div4","div5","div6","div7","div8","div9","div22","div23","div26","div32"] },

  { id: "demo_5", name: "Ardmore Mixed-Use Building", area: 24000, dateISO: monthsAgo(7),
    overhead: 8, profit: 7, contingency: 4, priceFactor: 0.88, seed: 505,
    divisionIds: ["div1","div2","div3","div5","div6","div7","div8","div9","div22","div23","div26","div32"] },

  { id: "demo_6", name: "Berwyn Pool House & Guest Suite", area: 2900, dateISO: monthsAgo(2),
    overhead: 13, profit: 9, contingency: 7, priceFactor: 1.22, seed: 606,
    divisionIds: ["div1","div3","div6","div7","div8","div9","div22","div23","div26","div32"] },
];

// ── Where the estimate was wrong, and by how much ───────────────────────────
// Without a deliberate pattern the Estimate Accuracy tab is noise. With one it
// tells a story an estimator recognises: concrete and framing keep beating the
// budget, the trades that were priced off allowances keep coming back under.
// Positive means the award landed OVER the budget (the estimate ran low).
//
// These are INPUTS, not the variances that come out. The award goes to the low
// bidder, so the realized variance lands roughly three points under the figure
// set here. They are tuned against the measured output — do not "correct" them
// to match the percentages shown in the Estimate Accuracy tab.

const DIVISION_BIAS: Record<string, number> = {
  div3: 0.0814,    // concrete — chronically under-budgeted
  div6: 0.0833,    // framing — the other chronic one
  div32: 0.0408,
  div4: 0.0664,
  div8: 0.0426,
  div7: 0.0282,
  div9: 0.0251,
  div5: -0.0041,
  div22: -0.0181,
  div23: -0.0331,
  div26: -0.0381,  // the most over-budgeted trade in the portfolio
};

const divMeta = (id: string) => ALL_DIVISIONS.find((d) => d.id === id)!;

function buildProject(spec: ProjectSpec): DemoProject {
  const rand = rng(spec.seed);
  const itemState: Record<string, any> = {};
  const divisions: any[] = [];
  const scaleK = spec.area / 1000;

  for (const divId of spec.divisionIds) {
    const subs = CATALOG[divId];
    if (!subs) continue;
    const meta = divMeta(divId);
    const subcategories = subs.map((sub, si) => {
      const rows = sub.items.map((it, ii) => {
        const id = `${spec.id}_${divId}_${si}_${ii}`;
        // ±8% so two projects never show an identical unit rate in the Cost
        // Library — real estimates never do.
        const jitter = 0.92 + rand() * 0.16;

        let baseQty = it.qtyFixed ?? (it.qtyPer1k ?? 0) * scaleK;
        // priceFactor says this job's WORK prices high or low — a renovation
        // costs more per square foot than new build. It must not touch staff
        // time: a superintendent earns a salary, not a market rate for the
        // job, and a $20,800/month super on a pool house is the single number
        // an estimator would spot as invented.
        let rate = it.unitCost * (it.unit === "MO" ? 1 : spec.priceFactor) * jitter;

        // A "fixed" cost is only fixed within a size bracket. A 12-month
        // superintendent and a $118k cabinetry allowance on a 2,900 SF pool
        // house pushed it to $867/SF, which an estimator would laugh at.
        // Duration-style units move in quantity; lump sums move in value.
        if (it.qtyFixed !== undefined) {
          const sizeFactor = Math.min(1.8, Math.max(0.35, spec.area / 10000));
          if (it.unit === "LS") rate *= sizeFactor;
          else baseQty *= sizeFactor;
        }

        const qty = Math.round(baseQty * 10) / 10;
        const unitCost = Math.round(rate * 100) / 100;
        itemState[id] = {
          description: it.desc, qty, unit: it.unit, unitCost,
          included: true, costStatus: it.status || "Hard Bid",
          altType: "", proposal: "p1", notes: "",
        };
        return { type: "item", id, description: it.desc, qty, unit: it.unit, unitCost, proposal: "p1", notes: "" };
      });
      return { id: `${spec.id}_${divId}_s${si}`, name: sub.name, costCode: sub.costCode, rows };
    });
    divisions.push({ id: divId, code: meta.code, name: meta.name, subcategories });
  }

  // ── Procurement: scopes, the partners who bid them, and what was awarded ──
  const bidScopes: BidScope[] = [];
  const bidPartners: BidPartner[] = [];
  const bids: Bid[] = [];
  const buyoutItems: BuyoutItem[] = [];
  const seenPartner = new Set<string>();

  const divisionBudget = (divId: string) => {
    const d = divisions.find((x) => x.id === divId);
    if (!d) return 0;
    let t = 0;
    for (const s of d.subcategories) for (const r of s.rows) {
      const it = itemState[r.id];
      if (it?.included !== false) t += (it.qty || 0) * (it.unitCost || 0);
    }
    return Math.round(t);
  };

  // Only divisions that are actually bought out get a package — Div 01 is the
  // GC's own cost and never goes out to bid.
  const buyoutDivs = spec.divisionIds.filter((d) => d !== "div1" && d !== "div2");

  buyoutDivs.forEach((divId, idx) => {
    const meta = divMeta(divId);
    const budget = divisionBudget(divId);
    if (budget <= 0) return;

    const scopeId = `${spec.id}_bs_${divId}`;
    bidScopes.push({ id: scopeId, name: `${meta.name} Package`, divisionIds: [divId] });

    const candidates = PARTNERS.filter((p) => p.divisionId === divId);
    const allPool = candidates.length ? candidates : [PARTNERS[idx % PARTNERS.length]];

    // Nobody bids everything. A sub who turns up on all six jobs with exactly
    // their competitor's win rate is the tell that the data was generated.
    // Two bidders is the floor — below that there is no package to level.
    const pool = allPool.length > 2
      ? allPool.filter((_, i) => i < 2 || rand() > 0.3)
      : allPool;

    const divBias = DIVISION_BIAS[divId] ?? 0;

    // Everyone prices it, the low number wins. Letting the winner fall out of
    // the pricing instead of assigning it is what makes win rates and
    // per-partner variance differ the way a real bid board does.
    const priced = pool.map((p, pi) => {
      if (!seenPartner.has(p.id)) {
        seenPartner.add(p.id);
        bidPartners.push({ id: p.id, name: p.name, trade: p.trade, contact: "", notes: "" });
      }
      const spread = divBias + p.bias + (rand() - 0.5) * 0.11;
      // Bids arrive over the weeks before the award, not all on one day.
      const d = new Date(spec.dateISO + "T00:00:00Z");
      d.setUTCDate(d.getUTCDate() - Math.floor(rand() * 21));
      return {
        partner: p,
        bidId: `${spec.id}_bid_${divId}_${pi}`,
        amount: Math.round(budget * (1 + spread)),
        date: d.toISOString().slice(0, 10),
      };
    });

    const low = priced.reduce((a, b) => (b.amount < a.amount ? b : a));
    let winningBid: { bidId: string; vendor: string; amount: number } | null = null;

    for (const b of priced) {
      const won = b.bidId === low.bidId;
      bids.push({
        id: b.bidId, partnerId: b.partner.id, scopeId, version: 1,
        date: b.date, status: won ? "Active" : "Rejected",
        items: [{ id: `${b.bidId}_i1`, description: `${meta.name} — complete scope`, qty: 1, unit: "LS", unitCost: b.amount, notes: "" }],
        pdfName: "", notes: "",
      });
      if (won) winningBid = { bidId: b.bidId, vendor: b.partner.name, amount: b.amount };
    }

    if (winningBid) {
      const w: { bidId: string; vendor: string; amount: number } = winningBid;
      buyoutItems.push({
        id: `${spec.id}_bo_${divId}`,
        scope: `${meta.name} Package`,
        divisionId: divId,
        budgetAmount: budget,
        vendor: w.vendor,
        bidId: w.bidId,
        contractAmount: w.amount,
        contractNumber: `${spec.id.toUpperCase()}-${meta.code.replace(/\D/g, "")}`,
        contractDate: spec.dateISO,
        status: "executed",
        notes: "",
      });
    }
  });

  return {
    id: spec.id, name: spec.name, dateISO: spec.dateISO,
    divisions, itemState,
    overhead: spec.overhead, profit: spec.profit, contingency: spec.contingency,
    buildingArea: spec.area,
    bids, bidPartners, bidScopes, buyoutItems,
  };
}

let cached: DemoProject[] | null = null;

/** The sample portfolio. Built once and reused — it never changes. */
export function buildDemoPortfolio(): DemoProject[] {
  if (!cached) cached = SPECS.map(buildProject);
  return cached;
}

/** For tests that want a clean build. */
export function _resetDemoPortfolioCache() { cached = null; }

// ── Standing in for the real storage provider ───────────────────────────────

/**
 * The two calls BenchmarkView makes, answered from the sample portfolio.
 *
 * Compare Projects loads its own data rather than taking it from the Insights
 * hub, so switching the hub to sample data left that one tab showing the
 * user's real projects under a banner announcing sample data — the worst of
 * both, and the bug this exists to fix.
 *
 * Deliberately only the two methods it calls. Anything else BenchmarkView
 * reaches for later throws immediately, which is far better than silently
 * going back to real data behind a sample-data banner.
 */
export function demoStorage(): any {
  const projects = buildDemoPortfolio();
  const fin = computePortfolioFinancials(projects as any);
  const byId = new Map(fin.projects.map((f) => [f.id, f]));

  return {
    listProjectsAsync: async () =>
      projects.map((p) => {
        const f = byId.get(p.id);
        return {
          id: p.id,
          name: p.name,
          total: f?.total ?? 0,
          costPerSF: f?.costPerSF ?? 0,
          savedAt: p.dateISO,
          sharedVia: "owner",
        };
      }),
    loadProjectById: async (id: string) => buildDemoPortfolio().find((p) => p.id === id) ?? null,
  };
}
